import React, { useState } from 'react'
import { t, Trans } from '@lingui/macro'
import { Box, Flex } from '@chakra-ui/react'
import CommentHeader from '../comment-header/CommentHeader'
import {
  ButtonIcon,
  ButtonOutline,
  ButtonPrimary
} from 'front-end/src/components'
import IconClose from 'front-end/src/components/common/icon/IconClose'
import IconPencil from 'front-end/src/components/common/icon/IconPencil'
import {
  useDeleteCommentMutation,
  useEditCommentMutation
} from './CommentBubble.codegen'
import { usePublicApiSession } from 'front-end/src/public-api/hooks/usePublicApiSession'
import { UserDetailFragment } from 'front-end/src/public-api/hooks/usePublicApiSession.codegen'

export interface IComment {
  id: number
  author: UserDetailFragment
  item_type: string
  item_id: number
  content: string
  reply_to: null
  created_at: string
  updated_at: string
}

export const CommentBubble = (props: IComment) => {
  const { userIsAdminOrManager, session } = usePublicApiSession()
  const [deleteCommentMutation] = useDeleteCommentMutation()
  const [updateCommentMutation] = useEditCommentMutation()
  const [state, setState] = useState({
    content: props.content,
    editing: false,
    deleted: false,
    showDeletePrompt: false
  })

  const changeState = (newState) => {
    setState({
      ...state,
      ...newState
    })
  }

  const updateComment = async () => {
    const { id } = props

    await updateCommentMutation({
      variables: {
        id,
        content: state.content
      }
    })

    changeState({
      editing: false
    })
  }

  const deleteComment = async () => {
    const { id } = props

    await deleteCommentMutation({
      variables: {
        id
      }
    })

    changeState({
      showDeletePrompt: false,
      deleted: true
    })
  }

  const canUserDelete = () => {
    return userIsAdminOrManager || authorIsCurrentUser()
  }
  const authorIsCurrentUser = () => {
    const { author } = props
    return author.id === session?.user.id
  }

  const renderCommentActions = () => {
    let editActionIcon: React.ReactElement | null = null
    let deleteActionIcon: React.ReactElement | null = null
    if (authorIsCurrentUser()) {
      editActionIcon = (
        <ButtonIcon
          className="comment-edit-button"
          circular
          aria-label={t`Edit`}
          hint={t`Edit`}
          onClick={() => changeState({ editing: true })}
        >
          <IconPencil />
        </ButtonIcon>
      )
    }
    if (canUserDelete()) {
      deleteActionIcon = (
        <ButtonIcon
          className="comment-delete-button"
          circular
          aria-label={t`Delete`}
          hint={t`Delete`}
          onClick={() => changeState({ showDeletePrompt: true })}
        >
          <IconClose />
        </ButtonIcon>
      )
    }
    return (
      <Box ml="auto">
        {editActionIcon}
        {deleteActionIcon}
        {state.showDeletePrompt && (
          <Box className="comment-delete">
            <p>
              <Trans>Delete this comment?</Trans>
            </p>
            <a
              className="btn btn-default"
              onClick={() =>
                changeState({
                  showDeletePrompt: false
                })
              }
            >
              <Trans>Cancel</Trans>
            </a>
            <a className="btn btn-danger" onClick={deleteComment}>
              <Trans>Delete</Trans>
            </a>
          </Box>
        )}
      </Box>
    )
  }

  if (state.deleted) {
    return null
  }

  const { author, content } = props
  let contentOrEditArea = <Box className="comment-content">{state.content}</Box>
  if (state.editing) {
    contentOrEditArea = (
      <Box className="comment-edit">
        <textarea
          onChange={(e) => (state.content = e.target.value)}
          name="content"
          placeholder={t`Your comment`}
        />

        <ButtonPrimary
          className="btn btn-success"
          onClick={updateComment}
          disabled={state.content === content}
        >
          <Trans>Save</Trans>
        </ButtonPrimary>
        <ButtonOutline
          className="btn btn-clear"
          onClick={() =>
            changeState({
              editing: false
            })
          }
        >
          <Trans>Cancel</Trans>
        </ButtonOutline>
      </Box>
    )
  }
  return (
    <Box className="comment">
      <Flex>
        <CommentHeader author={author} createdAt={props.created_at} />
        {renderCommentActions()}
      </Flex>
      {contentOrEditArea}
    </Box>
  )
}
